import { MessageSquare } from 'lucide-react'
import { formatUsPhone } from '@numeo/types-core'
import { SmsConversationSummary } from '@numeo/types'
import { TeamGroupSummary } from '@numeo/types-main-web-app'
import { Avatar, Badge, SearchField, Skeleton } from '@/components/ds2'
import { TeamGroupBadge } from '@/components/TeamGroupBadge'
import { fmtDateTime } from '../utils/format'
import { initialsOf, keyOf } from '../utils/smsThread'

/**
 * The filter value standing for "no desk".
 *
 * A sentinel rather than `null`, because `null` already means "no filter" — and it cannot collide with a
 * real option, since every group id is a Mongo ObjectId hex string.
 */
export const UNGROUPED = 'ungrouped'

/** The filter chip for conversations on no desk, and the badge their rows carry. One name for both, because
 *  they are the same bucket and an operator should not have to match two labels. */
const UNGROUPED_LABEL = 'No group'

/** Shaped like a {@link TeamGroupBadge} and deliberately NOT coloured like one: "no group" is the absence of
 *  a desk, and giving it a colour of its own would read as a desk called "No group". */
const UngroupedBadge = () => (
    <span className="inline-block max-w-full truncate rounded-full bg-default px-1.5 py-0.5 text-[11px] font-medium text-foreground-muted">
        {UNGROUPED_LABEL}
    </span>
)

/**
 * The SMS page's left column: every conversation this team has, newest first.
 *
 * Presentational — the page owns the list, the search term and which thread is open. A row is keyed by
 * `(line, counterpart)` because that is what a conversation IS: the same driver written to from two of
 * the company's numbers is two threads, and to them it is two people.
 */
export const SmsConversationList = ({
    conversations,
    loading,
    search,
    selected,
    multiLine,
    groupOf,
    groupOptions,
    hasUngrouped,
    groupFilter,
    onGroupFilter,
    onSearch,
    onOpen,
}: {
    /** Already filtered by the page's search and group — this renders what it is given. */
    conversations: SmsConversationSummary[]
    loading: boolean
    search: string
    /** The open thread's key, or null. */
    selected: string | null
    /** Whether the company has more than one Quo number; the "via" line is noise when it does not. */
    multiLine: boolean
    /** The desk a conversation's driver is on, or null for none — the page resolves it once for the list. */
    groupOf: (conversation: SmsConversationSummary) => TeamGroupSummary | null
    /** The desks present in this inbox, from the UNFILTERED list, in a stable order. */
    groupOptions: TeamGroupSummary[]
    /** Whether any conversation is on no desk — also from the unfiltered list, or selecting "No group"
     *  would empty the list and take its own chip away with it. */
    hasUngrouped: boolean
    /** The desk being shown: a group id, `UNGROUPED` for the no-desk bucket, or null for all. */
    groupFilter: string | null
    onGroupFilter: (groupId: string | null) => void
    onSearch: (term: string) => void
    onOpen: (conversation: SmsConversationSummary) => void
}) => {
    const filtered = conversations
    /**
     * Only where there is a choice to make, which is the rule the `via` line below already follows: on an
     * inbox that sits entirely on one desk every row would carry the same badge and every chip the same
     * list. Counted over the buckets, so one desk plus somebody outside it still counts as two.
     */
    const showGroups = groupOptions.length + (hasUngrouped ? 1 : 0) > 1
    const chipClass = (active: boolean) =>
        `shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors ${
            active ? 'border-accent bg-accent-soft/60 text-foreground' : 'border-border text-foreground-muted hover:bg-default/60'
        }`
    return (
        <div className="flex min-h-0 flex-col border-r border-border">
            <div className="border-b border-border p-3">
                <SearchField variant="secondary" value={search} onChange={onSearch} onClear={() => onSearch('')} placeholder="Search name or number…" />
                {showGroups && (
                    <div className="mt-2 flex gap-1 overflow-x-auto pb-0.5">
                        <button
                            type="button"
                            aria-pressed={groupFilter === null}
                            className={chipClass(groupFilter === null)}
                            onClick={() => onGroupFilter(null)}
                        >
                            All
                        </button>
                        {groupOptions.map((group) => (
                            <button
                                key={group.id}
                                type="button"
                                aria-pressed={groupFilter === group.id}
                                className={chipClass(groupFilter === group.id)}
                                onClick={() => onGroupFilter(group.id)}
                            >
                                {group.name}
                            </button>
                        ))}
                        {hasUngrouped && (
                            <button
                                type="button"
                                aria-pressed={groupFilter === UNGROUPED}
                                className={chipClass(groupFilter === UNGROUPED)}
                                onClick={() => onGroupFilter(UNGROUPED)}
                            >
                                {UNGROUPED_LABEL}
                            </button>
                        )}
                    </div>
                )}
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
                {loading ? (
                    <div className="space-y-1 p-3">
                        {[0, 1, 2, 3, 4].map((i) => (
                            <div key={i} className="flex items-center gap-2.5 py-2">
                                <Skeleton width={36} height={36} radius={999} />
                                <div className="flex-1 space-y-1.5">
                                    <Skeleton width="55%" height={11} radius={4} />
                                    <Skeleton width="80%" height={9} radius={4} />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
                        <div className="flex size-10 items-center justify-center rounded-full bg-accent-soft text-accent">
                            <MessageSquare size={18} />
                        </div>
                        <p className="text-xs text-foreground-muted">
                            {search ? 'Nobody matches that.' : 'No conversations yet.'}
                            {!search && (
                                <>
                                    {' '}
                                    Use <span className="font-medium text-foreground">New message</span> to text your drivers.
                                </>
                            )}
                        </p>
                    </div>
                ) : (
                    filtered.map((c) => {
                        const key = keyOf(c.line, c.counterpart)
                        const active = selected === key
                        const group = showGroups ? groupOf(c) : null
                        return (
                            <button
                                key={key}
                                type="button"
                                onClick={() => onOpen(c)}
                                className={`flex w-full items-center gap-2.5 border-l-2 px-3 py-2.5 text-left transition-colors ${
                                    active ? 'border-l-accent bg-accent-soft/40' : 'border-l-transparent hover:bg-default/50'
                                }`}
                            >
                                <Avatar variant="letterSoft" type={c.unread > 0 ? 'accent' : 'default'} value={initialsOf(c.name, c.counterpart)} size={36} />
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-baseline justify-between gap-2">
                                        <span className={`truncate text-sm ${c.unread > 0 ? 'font-semibold' : 'font-medium'}`}>
                                            {c.name || formatUsPhone(c.counterpart)}
                                        </span>
                                        <span className="shrink-0 text-[11px] tabular-nums text-foreground-muted">{fmtDateTime(c.lastAt)}</span>
                                    </div>
                                    <div className="mt-0.5 flex items-center gap-2">
                                        <span className={`line-clamp-1 flex-1 text-xs ${c.unread > 0 ? 'text-foreground' : 'text-foreground-muted'}`}>
                                            {/* Not every outbound is the desk's. "You: " on the load bot's answer
                                                read as a colleague having handled the driver, which is the one thing
                                                this line must not get wrong. */}
                                            {c.lastDirection === 'out' && (
                                                <span className={c.lastSource === 'bot' ? 'font-medium text-accent' : undefined}>
                                                    {c.lastSource === 'bot' ? 'Bot: ' : c.lastSource === 'quo_app' ? 'Quo: ' : 'You: '}
                                                </span>
                                            )}
                                            {c.lastMessage}
                                        </span>
                                        {c.unread > 0 && <Badge variant="primary" type="accent" label={String(c.unread)} />}
                                    </div>
                                    {/* Which of the company's numbers this conversation is on. Only worth the
                                        row's second line once there IS more than one — on a single-line
                                        workspace it is the same value on every row. */}
                                    {(multiLine || showGroups) && (
                                        <div className="mt-1 flex max-w-full flex-wrap items-center gap-1">
                                            {multiLine && (
                                                <span className="inline-flex max-w-full items-center gap-1 rounded-md bg-default px-1.5 py-0.5 text-[10px] text-foreground-muted">
                                                    <span className="opacity-70">via</span>
                                                    <span className="truncate font-medium">{formatUsPhone(c.line)}</span>
                                                </span>
                                            )}
                                            {/* The desk this driver is on. Absent is a real answer, not a gap:
                                                a number nobody on the roster answers to has no driver and so
                                                no desk, and it is in front of the whole team on purpose. */}
                                            {showGroups && (group ? <TeamGroupBadge group={group} truncate /> : <UngroupedBadge />)}
                                        </div>
                                    )}
                                </div>
                            </button>
                        )
                    })
                )}
            </div>
        </div>
    )
}
